/**
 * ArXiv license fetching service using OAI-PMH interface.
 * The standard arXiv API does not include license information,
 * so we must use the OAI-PMH interface with arXiv metadata format.
 */

import { 
  LicenseFetchResult, 
  LicenseFetchError,
  ARXIV_RATE_LIMIT_MS,
} from '@/types/license';
import { supabase } from '@/lib/supabase';

/**
 * Rate limiting state to enforce arXiv's 3-second requirement
 */
let lastRequestTime = 0;

/**
 * Enforces rate limiting between arXiv API calls (3 second minimum)
 */
async function enforceRateLimit(): Promise<void> {
  const now = Date.now();
  const elapsed = now - lastRequestTime;
  
  if (elapsed < ARXIV_RATE_LIMIT_MS) {
    const waitTime = ARXIV_RATE_LIMIT_MS - elapsed;
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  
  lastRequestTime = Date.now();
}

/**
 * Extracts arXiv ID from various URL formats
 * @param url ArXiv URL (abs or pdf format)
 * @returns ArXiv ID or null if invalid
 */
export function extractArxivId(url: string): string | null {
  if (!url || !(url.includes('arxiv.org') || url.includes('www.arxiv.org'))) {
    return null;
  }

  // Match standard arXiv ID patterns (e.g., 2401.00001, math-ph/0101001)
  // Accept both arxiv.org and www.arxiv.org
  const matches = [
    url.match(/(?:www\.)?arxiv\.org\/(?:abs|pdf)\/(\d+\.\d+)/), // New format: YYMM.NNNNN
    url.match(/(?:www\.)?arxiv\.org\/(?:abs|pdf)\/([a-z-]+\/\d+)/), // Old format: subject-class/YYMMnnn
  ];

  for (const match of matches) {
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}


/**
 * Fetches license information for an arXiv paper using OAI-PMH interface
 * @param arxivId ArXiv paper ID (e.g., "2401.00001")
 * @returns Promise resolving to license fetch result
 */
export async function fetchArxivLicense(arxivId: string): Promise<LicenseFetchResult> {
  try {
    // Enforce rate limiting before making request
    await enforceRateLimit();

    // Use Supabase edge function to avoid CORS issues
    const { data, error } = await supabase.functions.invoke('extract-arxiv-license', {
      body: { arxivId }
    });

    if (error) {
      console.error(`License fetch failed for ${arxivId}:`, error);
      return {
        success: false,
        error: `Failed to fetch license: ${error.message || 'Unknown error'}`,
        arxivId
      };
    }

    // The edge function returns the same format as this function expects
    return data as LicenseFetchResult;

  } catch (error) {
    console.error(`License fetch failed for ${arxivId}:`, error);
    
    if (error instanceof LicenseFetchError) {
      return {
        success: false,
        error: error.message,
        arxivId
      };
    }

    return {
      success: false,
      error: `Unexpected error fetching license: ${error instanceof Error ? error.message : 'Unknown error'}`,
      arxivId
    };
  }
}

/**
 * Fetches license information from arXiv URL
 * @param url ArXiv paper URL
 * @returns Promise resolving to license fetch result
 */
export async function fetchArxivLicenseFromUrl(url: string): Promise<LicenseFetchResult> {
  const arxivId = extractArxivId(url);
  
  if (!arxivId) {
    return {
      success: false,
      error: 'Could not extract valid arXiv ID from URL. Format should be: https://arxiv.org/abs/XXXX.XXXXX',
      arxivId: 'invalid'
    };
  }

  return fetchArxivLicense(arxivId);
}

/**
 * Batch fetch licenses for multiple arXiv papers with rate limiting
 * @param arxivIds Array of arXiv paper IDs
 * @param progressCallback Optional callback for progress updates
 * @returns Promise resolving to array of license fetch results
 */
export async function batchFetchArxivLicenses(
  arxivIds: string[],
  progressCallback?: (completed: number, total: number) => void
): Promise<LicenseFetchResult[]> {
  const results: LicenseFetchResult[] = [];
  
  for (let i = 0; i < arxivIds.length; i++) {
    const arxivId = arxivIds[i];
    
    try {
      const result = await fetchArxivLicense(arxivId);
      results.push(result);
      
      if (progressCallback) {
        progressCallback(i + 1, arxivIds.length);
      }
      
    } catch (error) {
      console.error(`Batch license fetch failed for ${arxivId}:`, error);
      results.push({
        success: false,
        error: `Batch fetch error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        arxivId
      });
    }
  }
  
  return results;
}